import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, ChevronRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Compact overdue-leads rollup for the Dashboard. Shows total + a per-stage
 * breakdown and links straight into the Leads page's "Overdue only" view.
 *
 * Hits GET /api/reports/overdue-leads which is already hierarchy-scoped —
 * sales reps only see their own, managers see their reports, admins see all.
 */
export default function OverdueLeadsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/overdue-leads");
        setData(res.data);
      } catch (_e) {
        setData({ total: 0, by_stage: [], leads: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-lg p-5" data-testid="overdue-widget-loading">
        <div className="h-4 w-32 bg-slate-100 rounded animate-pulse" />
        <div className="mt-3 h-16 bg-slate-50 rounded animate-pulse" />
      </section>
    );
  }

  const total = data?.total || 0;
  const empty = total === 0;

  return (
    <section
      className={`border rounded-lg overflow-hidden ${empty ? "bg-white border-slate-200" : "bg-rose-50/60 border-rose-200"}`}
      data-testid="overdue-leads-widget"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${empty ? "bg-emerald-50 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Overdue leads</div>
            <div className="text-xs text-slate-500">Past stage SLA · business days only (Mon–Fri)</div>
          </div>
        </div>
        <Link
          to="/leads?overdue=1"
          className="text-xs text-rose-600 hover:text-rose-700 font-semibold inline-flex items-center gap-1"
          data-testid="overdue-widget-view-link"
        >
          View all <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {empty ? (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="overdue-widget-empty">
          <Clock className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
          No overdue leads in your pipeline. Nicely done.
        </div>
      ) : (
        <div className="divide-y divide-rose-100">
          <div className="px-5 py-3 flex items-baseline justify-between">
            <div className="text-3xl font-heading font-semibold text-rose-700 leading-none" data-testid="overdue-widget-total">{total}</div>
            <div className="text-xs text-rose-700/80">lead{total === 1 ? "" : "s"} need attention</div>
          </div>
          <ul className="px-5 py-3 space-y-1.5">
            {(data?.by_stage || []).map((row) => (
              <li
                key={row.stage}
                className="flex items-center justify-between text-sm"
                data-testid={`overdue-widget-stage-${row.stage.replace(/\s+/g, "-")}`}
              >
                <span className="text-slate-700">
                  <span className="font-medium">{row.stage}</span>
                  <span className="text-xs text-slate-500 ml-2">SLA {row.sla} bd</span>
                </span>
                <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 bg-rose-100 border border-rose-200 text-rose-700 rounded text-xs font-semibold">
                  {row.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
